import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAppSelector } from './hooks/ReduxHooks';
import { MessageAction, MessageState, closeMessage } from './helpers/MessageUtility';
import CSSTransition from 'react-transition-group/CSSTransition';
import { Link, useLocation } from 'react-router-dom';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import GfModal from './gfcomponents/GfModal/GfModal';
import { TextArea } from 'semantic-ui-react';
import { FormatMessage } from './components/FormatMessage';
import PromptTextBox from './PromptTextBox';
import { useUserPersistedState } from './persistedstate/users/useUserPersistedState';
import { useImmerState } from './hooks/useImmerState';

export type TrackedMessageState = { [id: number]: { text: string; errorMessage?: string } };

export default function MessagesV2() {
  const messages = useAppSelector<MessageState[]>((state) => state.messages || []);
  const [trackedMessageState, setTrackedMessageState] = useImmerState<TrackedMessageState>({}); //handles temporary UI state
  const inputRef = useRef<HTMLInputElement | TextArea>();

  const [skipConfirmationModal, setSkipConfirmationModal] = useUserPersistedState('skipConfirmationModal', {});
  const [confirmIds, setConfirmIds] = useState<number[]>([]);
  const location = useLocation();
  const [initialLocation] = useState(location);

  // close the showConfirm modal if the user navigates away from the page
  useEffect(() => {
    const handleNavigationOrUnmount = () => {
      if ((confirmIds || []).length > 0) {
        confirmIds.forEach((id) => closeMessage(id));
      }
    };

    // Listen for navigation events
    if (location !== initialLocation) {
      handleNavigationOrUnmount();
    }

    return handleNavigationOrUnmount;
  }, [location, initialLocation]);

  useEffect(() => {
    // set up the initial state for the messages
    setTrackedMessageState((trackedMessageState) => {
      // Check for new messages that aren't in our tracked state yet
      messages.forEach((message) => {
        // Only add messages that aren't already being tracked
        if (!trackedMessageState[message.id]) {
          trackedMessageState[message.id] = { text: message.text };
        }
      });
    });
  }, [messages, setTrackedMessageState]);

  useLayoutEffect(() => {
    inputRef.current?.focus();
  }, [messages]);

  const addConfirmId = (newId: number) => {
    setConfirmIds((prevIds) => [...prevIds, newId]);
  };

  const handlePromptValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
      setTrackedMessageState((trackedMessageState) => {
        // Create or update the tracked message
        if (!trackedMessageState[id]) {
          trackedMessageState[id] = { text: e.target.value };
        } else {
          trackedMessageState[id].text = e.target.value;
        }

        // Validate the input value
        const message = messages.find((m) => m.id === id);
        if (message?.onValidateInput) {
          const validation = message.onValidateInput(e.target.value);
          trackedMessageState[id].errorMessage = validation.errorMessage;
        }
      });
    },
    [messages, setTrackedMessageState]
  );

  const promptOk = useCallback(
    async (id: number) => {
      await closeMessage(id, true, trackedMessageState[id].text, false);
      setTrackedMessageState((trackedMessageState) => {
        delete trackedMessageState[id];
      });
    },
    [trackedMessageState, setTrackedMessageState]
  );

  const handleKeydown = useCallback(
    (e: React.KeyboardEvent, id: number) => {
      if (e.key === 'Enter') {
        promptOk(id);
      }
    },
    [promptOk]
  );

  if (messages.length === 0) {
    return null;
  }

  const messageBoxes = [];
  let bannerMessage: JSX.Element;

  for (const message of messages) {
    if (message.type === 'banner') {
      bannerMessage = (
        <CSSTransition classNames="banner-message" timeout={200}>
          <div key={message.id} className={`banner-message ${message.level || 'success'}`}>
            <div className="icon" />
            <div className="message">
              {message.message}
              {message.linkText && message.linkUrl ? (
                <>
                  {' '}
                  <Link to={message.linkUrl} onClick={() => closeMessage(message.id)}>
                    {message.linkText}
                  </Link>
                </>
              ) : null}
            </div>
            <button
              className="dismiss icon-close"
              onClick={() => {
                closeMessage(message.id);
              }}
            />
          </div>
        </CSSTransition>
      );

      continue;
    }

    // for confirm messages, we need to set the confirmId so we can close it when the user navigates away from the page
    if (message.type === 'confirm' && !(confirmIds || []).includes(message.id)) {
      addConfirmId(message.id);
    }

    // if this is a confirm message and we've already shown it and the user checked to not show it, tnen don't show it again
    // auto call the confirm function
    if (message.type === 'confirm' && skipConfirmationModal?.[message.skipConfirmationModalKey] === true) {
      closeMessage(message.id, true, null, false);
      continue;
    }

    // if there is a skipConfirmationModalKey then render a checkbox for the user to check to not show this message again
    const actionButtonProps = message.skipConfirmationModalKey && {
      checkbox: {
        label: `Don't show this again`
      }
    };

    const isPrompt = message.type === 'prompt';
    const inputText = trackedMessageState[message.id]?.text || '';
    const hasError = !!trackedMessageState[message.id]?.errorMessage;
    const isEmptyInput = isPrompt && inputText.trim() === '';

    // since it's not a banner, it's either an alert, confirm or prompt modal
    messageBoxes.push(
      <GfModal
        key={message.id}
        title={message.title}
        className={message.className || ''}
        primaryText={message.type === 'confirm' ? message.yesText : message.okText}
        secondaryText={message.noText}
        tertiaryText={message.cancelText}
        loading={message.working}
        actionButtonProps={{
          ...actionButtonProps,
          primary: {
            // Disable primary button if there's an error or if it's a prompt with empty input
            disabled: hasError || isEmptyInput
          }
        }}
        onClose={() => closeMessage(message.id, false, null, true, MessageAction.CLOSE)}
        onSecondaryClick={() => closeMessage(message.id, false, null, false, MessageAction.SECONDARY)}
        onTertiaryClick={() => closeMessage(message.id, false, null, false, MessageAction.TERTIARY)}
        onPrimaryClick={(e) => {
          if (message.skipConfirmationModalKey && e.checkboxChecked) {
            setSkipConfirmationModal({ ...skipConfirmationModal, [message.skipConfirmationModalKey]: true });
          }
          message.type === 'prompt' ? promptOk(message.id) : closeMessage(message.id, true, null, false, MessageAction.PRIMARY);
        }}
      >
        {message.message && (
          <div style={{ marginBottom: '8px' }}>
            <FormatMessage tooltip={message.messageTooltip}>{message.message}</FormatMessage>
          </div>
        )}
        {message.type === 'prompt' && (
          <PromptTextBox
            message={message}
            state={trackedMessageState}
            inputRef={inputRef}
            onPromptValueChange={handlePromptValueChange}
            onKeyDown={handleKeydown}
            errorMessage={trackedMessageState[message.id]?.errorMessage}
          />
        )}
        {message.subText && <div style={{ marginTop: '8px' }}>{message.subText}</div>}
      </GfModal>
    );
  }

  return (
    <>
      {bannerMessage && <TransitionGroup>{bannerMessage}</TransitionGroup>}
      {messageBoxes}
    </>
  );
}
